'use client';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState, useTransition } from 'react';
import toast from 'react-hot-toast';

import { UpdatePasswordStep } from './update-password-step.ui';
import { RegisterStep } from './register-step.ui';
import { PasswordStep } from './password-step.ui';
import { OtpStep } from './otp-step.ui';
import { EmailStep } from './email-step.ui';

import { loginServerAction } from '@/slw/entities/auth/auth.action';
import {
  useChangePassword,
  useConfirmOtpForForgotPassword,
  useForgotPassword,
  useRegister,
  useResendOtp,
  useVerifyOtp,
} from '@/slw/entities/auth/auth.mutation';
import { useCheckEmailExist } from '@/slw/entities/auth/auth.query';
import { RegisterFormValues, UpdatePasswordFormValues } from '@/slw/entities/auth/auth.type';
import { useAuthFlow } from '@/slw/features/auth/use-auth-flow';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/slw/shared/ui/dialog';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { saveTokenClient } from '@/slw/shared/lib/auth.client';

export function AuthModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { state, setStep, setAuthType, setEmail, setExpires, setError, reset, setData } = useAuthFlow();
  const [resendCooldown, setResendCooldown] = useState(0);
  const router = useRouter();
  const {
    isFetching: isCheckingEmail,
    data: checkEmailData,
    isSuccess: isCheckEmailSuccess,
  } = useCheckEmailExist(state.email);
  const fcmToken = useAuthStore((state) => state.fcmToken);
  const [isPending, startTransition] = useTransition();
  const verifyOtpMutation = useVerifyOtp();
  const registerMutation = useRegister();
  const resendOtpMutation = useResendOtp();
  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      toast.success('Password updated successfully');
    },
  });
  const forgotPasswordMutation = useForgotPassword();
  const confirmOtpForForgotPasswordMutation = useConfirmOtpForForgotPassword();

  useEffect(() => {
    if (open) setStep('email');
  }, [open, setStep]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const interval = setInterval(() => setResendCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);

      return () => clearInterval(interval);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleCheckEmail = async (email: string) => {
    setEmail(email);
  };

  useEffect(() => {
    if (isCheckEmailSuccess) {
      if (checkEmailData.data?.exists) {
        setStep('password');
        setAuthType('login');
      } else {
        setStep('register');
        setAuthType('register');
      }

      setData({ sessionId: checkEmailData.data?.sessionId || '' });
    } else {
      setError(checkEmailData?.message || 'Something went wrong');
    }
  }, [isCheckEmailSuccess, checkEmailData, setStep, setError, setData, setAuthType]);

  const handleLogin = useCallback(
    (password: string, isRemembered: boolean) => {
      startTransition(async () => {
        try {
          const response = await loginServerAction({ 
            username: state.email, 
            password, 
            fcmToken,
            isRemembered 
          });

          // Save tokens to httpOnly cookies (more secure)
          saveTokenClient(response.data.token);
          
          // Save refresh token to localStorage for client-side refresh logic
          if (response.data.refreshToken) {
            localStorage.setItem('refresh_token', response.data.refreshToken);
          }
          
          // Save token expiry time
          if (response.data.expiresAt) {
            localStorage.setItem('token_expires_at', response.data.expiresAt);
          }
          
          setData(response.data);
          onClose();

          if (state.step === 'password') {
            toast.success('Login Successfully');
          } else {
            toast.success('Successful registration');
          }

          // window.location.href = '/';

          return;
        } catch (error) {

          //  toast.error((error as Error)?.message || 'Something went wrong');
        }
      });
    },
    [state.email, state.step, fcmToken, setData, onClose],
  );

  const handleRegister = (data: RegisterFormValues) => {
    startTransition(async () => {
      try {
        const response = await registerMutation.mutateAsync({ ...data, sessionId: state.sessionId });

        if (response.success) {
          setData({ password: data.password });
          setResendCooldown(60);
          setStep('otp');
        } else {
          setError(response.message || 'Something went wrong');
        }
      } catch (error) {
        setError((error as Error)?.message || 'Something went wrong');
      }
    });
  };

  const handleOtp = useCallback(
    async (otpCode: string) => {
      startTransition(async () => {
        try {
          setError();

          if (state.authType === 'register') {
            const res = await verifyOtpMutation.mutateAsync({ sessionId: state.sessionId, otpCode });

            if (res.success) {
              handleLogin(state.password || '');
            }
          } else {
            const res = await confirmOtpForForgotPasswordMutation.mutateAsync({
              sessionId: state.sessionId,
              otpCode,
            });

            if (res.success) {
              setStep('update-password');
            }
          }
        } catch (error) {
          const errorMessage = (error as Error)?.message || 'Your code is invalid, please enter again';

          // toast.error(errorMessage);
          setError(errorMessage);
        }
      });
    },
    [
      setError,
      verifyOtpMutation,
      confirmOtpForForgotPasswordMutation,
      state.sessionId,
      state.authType,
      state.password,
      handleLogin,
      setStep,
    ],
  );

  const handleResendOtp = async () => {
    startTransition(async () => {
      try {
        const otpRes = await resendOtpMutation.mutateAsync({ email: state.email, sessionId: state.sessionId });

        if (otpRes.success) {
          setExpires(180);
          setResendCooldown(60);
        }
      } catch (error) {
        setError((error as Error)?.message || 'Something went wrong');
      }
    });
  };

  const handleForgot = async () => {
    startTransition(async () => {
      try {
        const res = await forgotPasswordMutation.mutateAsync(state.email);

        if (res.success) {
          setData({ sessionId: res.data.sessionId });
          setExpires(180);
          setResendCooldown(60);
          setStep('otp');
          setAuthType('forgot');
        }
      } catch (error) {
        setError((error as Error)?.message || 'Something went wrong');
      }
    });
  };

  const handleUpdatePassword = async (values: UpdatePasswordFormValues) => {
    startTransition(async () => {
      try {
        const res = await changePasswordMutation.mutateAsync({
          sessionId: state.sessionId || '',
          newPassword: values.password,
        });

        if (res.success) {
          onClose();
          router.push('/');
        } else {
          setError(res.message || 'Something went wrong');
        }
      } catch (error) {
        setError((error as Error)?.message || 'Something went wrong');
      }
    });
  };

  let content = null;
  let title = '';

  if (state.step === 'email') {
    title = 'Login/ Sign up to continue';
    content = <EmailStep onSubmit={handleCheckEmail} loading={isCheckingEmail} />;
  } else if (state.step === 'password') {
    title = 'Login/ Sign up to continue';
    content = <PasswordStep email={state.email} onSubmit={handleLogin} loading={isPending} onForgot={handleForgot} />;
  } else if (state.step === 'register') {
    title = 'Sign up to continue';
    content = <RegisterStep email={state.email} onSubmit={handleRegister} loading={isPending} />;
  } else if (state.step === 'otp') {
    if (state.authType === 'register') {
      title = 'Sign up to continue';
    } else {
      title = 'Forgot Password';
    }

    content = (
      <OtpStep
        setError={setError}
        onSubmit={handleOtp}
        error={state.error}
        onResend={handleResendOtp}
        resendCooldown={resendCooldown}
      />
    );
  } else if (state.step === 'update-password') {
    title = 'Update Password';
    content = <UpdatePasswordStep email={state.email} onSubmit={handleUpdatePassword} loading={isPending} />;
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='p-0 w-full max-w-full md:w-[41.75rem] md:min-h-[17.125rem] px-6 py-8'>
        <DialogHeader>
          <DialogTitle className='font-bold text-[1.5rem] leading-[1.5] tracking-[-0.02em]'>{title}</DialogTitle>
          <DialogDescription className='sr-only'>{title}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
